import React, { useCallback, useEffect, useState } from 'react';
import { dataQualityService } from '../../services/dataQualityService';
import DataQualityDataViewerModal from '../DataQualityDataViewerModal';
import QualityOverview from './QualityOverview';
import EnrichDataCard from './EnrichDataCard';
import LegacyQualityPanel from './LegacyQualityPanel';

// The quality view, in one drill-down: how complete each record type is → field by
// field → the actual rows. The profile is baked onto each run (lineage.run.quality),
// so this is a lookup rather than a re-scan and there is nothing slow to hide.

export default function DataQualityPanel({ coverage, isAdmin, reloadKey = 0, onRunEnrichment, busy, job }) {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState({ open: false, tableName: null, fieldPairName: null });

  const runId = latest?.run?.dq_profile_run_id;
  const overview = latest?.overview || null;
  const tables = latest?.tables || [];

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setLatest(await dataQualityService.getLatest());
    } catch (e) {
      setError(e.message || 'Could not load the quality profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile, reloadKey]);

  const handleRecalculate = useCallback(async () => {
    setRecalculating(true);
    setError('');
    try {
      await dataQualityService.recalculate(latest?.run?.batch_run_id || null);
      await loadProfile();
    } catch (e) {
      setError(e.message || 'Recalculation failed.');
    } finally {
      setRecalculating(false);
    }
  }, [latest, loadProfile]);

  const openViewer = useCallback((tableName, fieldPairName) => {
    setViewer({ open: true, tableName, fieldPairName });
  }, []);

  return (
    <div className="space-y-6">
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : null}

      <QualityOverview
        overview={overview}
        tables={tables}
        coverage={coverage}
        runId={runId}
        loading={loading}
        onViewData={openViewer}
        belowStats={
          onRunEnrichment ? (
            <EnrichDataCard coverage={coverage} onRunEnrichment={onRunEnrichment} busy={busy} job={job} />
          ) : null
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          {loading
            ? 'Loading the latest profile…'
            : latest?.run
            ? `Profiled ${new Date(latest.run.completed_at || latest.run.started_at).toLocaleString()}`
            : 'No profile has run yet.'}
        </p>
        {isAdmin ? (
          <button
            type="button"
            onClick={handleRecalculate}
            disabled={recalculating || loading}
            className="px-4 py-2 rounded-lg bg-[#00C6AE] text-white text-sm font-semibold hover:bg-[#00B39E] disabled:opacity-60"
          >
            {recalculating ? 'Recalculating…' : 'Recalculate now'}
          </button>
        ) : null}
      </div>

      {/* "Where your data came from" used to live here as a second, separate view.
          It answered the same question the overview now answers in place — source vs
          platform-added — so keeping both meant reading the same numbers twice, in two
          different shapes. The provenance split moved INTO the completeness bar. */}

      {/* Deprecated. Kept only because it still shows the raw→cleaned comparison, which
          the view above drops. Remove once "created / rejected / extracted" replaces it. */}
      {!loading && tables.length > 0 ? (
        <LegacyQualityPanel overview={overview} tables={tables} runId={runId} onViewData={openViewer} />
      ) : null}

      <DataQualityDataViewerModal
        open={viewer.open}
        onClose={() => setViewer({ open: false, tableName: null, fieldPairName: null })}
        runId={runId}
        tableName={viewer.tableName}
        fieldPairName={viewer.fieldPairName}
      />
    </div>
  );
}
